import { useState } from 'react';
import { useFetch } from '@/hooks/useFetch';
import { sdk } from '@/services/sdk';
import { useAuth } from '@/layouts/RootLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const WORKER_FIELDS = ['Name', 'phone_c', 'status_c', 'onboardingStatus_c', 'aadhaarLast4_c', 'aadhaarVerificationStatus_c', 'drivingLicenseLast4_c', 'licenseVerificationStatus_c', 'bankAccountLast4_c', 'bankIfsc_c', 'bankVerificationStatus_c'];
const PAYMENT_FIELDS = ['Name', 'Owner', 'jobReference_c', 'paymentMode_c', 'amountReceived_c', 'paymentStatus_c', 'providerVerified_c', 'commissionAmount_c', 'cashPayableToAryanFix_c', 'agentEarnings_c', 'cashSettlementStatus_c', 'recordedByAgent_c'];
const RULE_FIELDS = ['Name', 'serviceCategory_c', 'ratePercent_c', 'effectiveFrom_c', 'isActive_c', 'version_c'];

export const route = { path: '/admin-reconciliation', layout: 'owner', access: "profile === 'Admin_c'" };
export const nav = { icon: 'ShieldCheck', label: 'Admin reconciliation', section: 'Administration', profiles: ['Admin_c'], order: 1 };

export default function AdminReconciliation() {
  const { user } = useAuth();
  const [category, setCategory] = useState('Electrical');
  const [rate, setRate] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [saving, setSaving] = useState('');
  const [remittanceConfirm, setRemittanceConfirm] = useState('');
  const { data: workers, loading: workersLoading, run: refreshWorkers } = useFetch(() => sdk.table('workers_c').select(WORKER_FIELDS).orderByDesc('CreatedOn').fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load worker verification queue');
    return r.data;
  }), []);
  const { data: payments, loading: paymentsLoading, run: refreshPayments } = useFetch(() => sdk.table('payments_c').select(PAYMENT_FIELDS).orderByDesc('CreatedOn').fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load payment queue');
    return r.data;
  }), []);
  const { data: rules, loading: rulesLoading, run: refreshRules } = useFetch(() => sdk.table('commission_rules_c').select(RULE_FIELDS).orderByDesc('CreatedOn').fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load commission rules');
    return r.data;
  }), []);
  const { data: walletRows, run: refreshWallet } = useFetch(() => sdk.table('wallet_transactions_c').select(['Name', 'paymentReference_c', 'idempotencyKey_c']).orderByDesc('CreatedOn').limit(500, 0).fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load wallet ledger');
    return r.data;
  }), []);
  const busy = workersLoading || paymentsLoading || rulesLoading;

  async function logAction(action, table, id, details) {
    return sdk.table('audit_logs_c').create({
      Name: action + ': ' + id,
      actorUserId_c: user?.userId || 'unknown',
      actorProfile_c: 'Admin_c',
      action_c: action,
      entityType_c: table,
      entityId_c: String(id),
      afterJson_c: JSON.stringify(details || {}),
      source_c: 'Agent portal admin',
      createdAt_c: new Date().toISOString()
    });
  }

  async function updateWorker(worker, changes, action) {
    setSaving(String(worker.Id));
    try {
      const result = await sdk.table('workers_c').update({ Id: worker.Id, ...changes });
      if (result.messages?.length) toast.error(result.messages[0]);
      if (!result.data?.length) return;
      try { await logAction(action, 'workers_c', worker.Id, changes); }
      catch (error) { toast.warning('Worker updated, but audit logging failed.'); }
      toast.success('Worker verification record updated.');
      refreshWorkers();
    } catch (error) {
      toast.error(error.message || 'Could not update worker.');
    } finally {
      setSaving('');
    }
  }

  async function createRule(event) {
    event.preventDefault();
    const percent = Number(rate);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) { toast.error('Commission must be between 0 and 100%.'); return; }
    if (!effectiveFrom) { toast.error('Choose the effective start date and time.'); return; }
    setSaving('rule');
    try {
      const version = Math.max(0, ...(rules ?? []).filter((rule) => rule.serviceCategory_c === category).map((rule) => Number(rule.version_c) || 0)) + 1;
      const result = await sdk.table('commission_rules_c').create({
        Name: category + ' commission rule v' + version,
        serviceCategory_c: category,
        ratePercent_c: percent,
        effectiveFrom_c: new Date(effectiveFrom).toISOString(),
        isActive_c: true,
        approvedBy_c: user?.userId || 'unknown',
        approvedAt_c: new Date().toISOString(),
        version_c: version,
        notes_c: 'Created by authorized Agent portal Admin.'
      });
      if (result.messages?.length) toast.error(result.messages[0]);
      if (!result.data?.length) return;
      try { await logAction('COMMISSION_RULE_CREATED', 'commission_rules_c', result.data[0].Id, { category, ratePercent_c: percent, version_c: version }); }
      catch (error) { toast.warning('Rule saved, but audit logging failed.'); }
      toast.success('Commission rule saved.');
      setRate('');
      refreshRules();
    } catch (error) {
      toast.error(error.message || 'Could not create commission rule.');
    } finally {
      setSaving('');
    }
  }

  async function reconcileCash(payment) {
    if (payment.paymentMode_c !== 'Cash') return;
    setSaving(String(payment.Id));
    try {
      if (payment.paymentStatus_c !== 'Verified') {
        const update = await sdk.table('payments_c').update({ Id: payment.Id, paymentStatus_c: 'Verified', providerVerified_c: false, cashSettlementStatus_c: 'Due' });
        if (update.messages?.length) toast.error(update.messages[0]);
        if (!update.data?.length) return;
        try { await logAction('CASH_PAYMENT_RECONCILED', 'payments_c', payment.Id, { paymentStatus_c: 'Verified', cashSettlementStatus_c: 'Due' }); }
        catch (error) { toast.warning('Payment updated, but audit logging failed.'); }
      }
      const exists = (walletRows ?? []).some((row) => row.paymentReference_c === String(payment.Id) && row.idempotencyKey_c === String(payment.Id) + ':earning');
      if (!exists) {
        const agentId = payment.Owner?.Id || payment.recordedByAgent_c;
        if (!agentId) { toast.error('Could not identify the Agent account for the wallet credit.'); return; }
        const credit = await sdk.table('wallet_transactions_c').create({
          Name: 'Agent earning for job ' + String(payment.jobReference_c),
          agentUserId_c: String(agentId),
          agent_c: String(agentId),
          jobReference_c: String(payment.jobReference_c),
          paymentReference_c: String(payment.Id),
          transactionType_c: 'Earning',
          amount_c: Number(payment.agentEarnings_c) || 0,
          direction_c: 'Credit',
          status_c: 'Approved',
          idempotencyKey_c: String(payment.Id) + ':earning',
          approvedBy_c: user?.userId || 'unknown',
          approvedAt_c: new Date().toISOString(),
          notes_c: 'Created after Admin cash reconciliation.'
        });
        if (credit.messages?.length) toast.error(credit.messages[0]);
        if (!credit.data?.length) { toast.error('Payment is verified, but the wallet credit failed. Retry to complete it.'); return; }
      }
      toast.success('Cash payment reconciled and Agent earning credited.');
      refreshPayments();
      refreshWallet();
    } catch (error) {
      toast.error(error.message || 'Could not reconcile cash payment.');
    } finally {
      setSaving('');
    }
  }

  async function markCashRemitted(payment) {
    setSaving(String(payment.Id));
    try {
      const result = await sdk.table('payments_c').update({ Id: payment.Id, cashSettlementStatus_c: 'Remitted' });
      if (result.messages?.length) toast.error(result.messages[0]);
      if (!result.data?.length) return;
      try { await logAction('CASH_REMITTANCE_CONFIRMED', 'payments_c', payment.Id, { cashSettlementStatus_c: 'Remitted' }); }
      catch (error) { toast.warning('Settlement updated, but audit logging failed.'); }
      toast.success('Cash remittance marked as received.');
      setRemittanceConfirm('');
      refreshPayments();
    } catch (error) {
      toast.error(error.message || 'Could not update cash settlement.');
    } finally {
      setSaving('');
    }
  }

  return (
    <div className="space-y-7">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Restricted administration</p>
          <h1 className="mt-2 font-heading text-3xl font-bold">Verification & reconciliation</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Admin-only controls for worker verification, commission rules and cash reconciliation. Online payments remain pending until provider verification is connected.</p>
        </div>
        <Button variant="outline" onClick={() => { refreshWorkers(); refreshPayments(); refreshRules(); refreshWallet(); }} disabled={busy}>{busy ? 'Refreshing…' : 'Refresh all'}</Button>
      </header>

      <section className="glass-panel rounded-[24px] p-5 sm:p-7">
        <h2 className="font-heading text-xl font-bold">Worker verification queue</h2>
        <p className="mt-1 text-sm text-muted-foreground">Only last-four-digit identifiers are shown. Confirm original documents out of band before recording verification.</p>
        <div className="mt-4 space-y-3">
          {(workers ?? []).map((worker) => (
            <article key={worker.Id} className="rounded-xl border border-border p-4">
              <p className="font-semibold">{worker.Name}</p>
              <p className="mt-1 text-xs text-muted-foreground">{worker.phone_c || 'No phone'} · {worker.status_c || 'Status pending'} · onboarding {worker.onboardingStatus_c || '—'}</p>
              <p className="mt-2 text-xs text-muted-foreground">Aadhaar •••• {worker.aadhaarLast4_c || '—'} ({worker.aadhaarVerificationStatus_c || 'Not submitted'}) · DL •••• {worker.drivingLicenseLast4_c || '—'} ({worker.licenseVerificationStatus_c || 'Not submitted'})</p>
              <p className="mt-1 text-xs text-muted-foreground">Bank •••• {worker.bankAccountLast4_c || '—'} · IFSC {worker.bankIfsc_c || '—'} · {worker.bankVerificationStatus_c || 'Not submitted'}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={saving === String(worker.Id) || !worker.aadhaarLast4_c || !worker.drivingLicenseLast4_c} onClick={() => updateWorker(worker, { aadhaarVerificationStatus_c: 'Verified', licenseVerificationStatus_c: 'Verified' }, 'WORKER_ID_DOCUMENTS_VERIFIED')}>Confirm ID documents checked</Button>
                <Button size="sm" variant="outline" disabled={saving === String(worker.Id) || !worker.bankAccountLast4_c || !worker.bankIfsc_c} onClick={() => updateWorker(worker, { bankVerificationStatus_c: 'Verified' }, 'WORKER_BANK_DETAILS_VERIFIED')}>Confirm bank checked</Button>
                <Button size="sm" disabled={saving === String(worker.Id) || worker.aadhaarVerificationStatus_c !== 'Verified' || worker.licenseVerificationStatus_c !== 'Verified' || worker.bankVerificationStatus_c !== 'Verified'} onClick={() => updateWorker(worker, { status_c: 'Active', onboardingStatus_c: 'Active' }, 'WORKER_ACTIVATED')}>Activate worker</Button>
                <Button size="sm" variant="destructive" disabled={saving === String(worker.Id)} onClick={() => updateWorker(worker, { status_c: 'Inactive', onboardingStatus_c: 'Rejected' }, 'WORKER_REJECTED')}>Reject</Button>
              </div>
            </article>
          ))}
          {!workersLoading && !(workers ?? []).length && <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No worker onboarding records are waiting.</p>}
        </div>
      </section>

      <section className="glass-panel rounded-[24px] p-5 sm:p-7">
        <h2 className="font-heading text-xl font-bold">Owner-approved commission rules</h2>
        <p className="mt-1 text-sm text-muted-foreground">Rules are versioned and snapshotted on each future payment submission. Existing payments keep their original rate.</p>
        <form onSubmit={createRule} className="mt-4 grid gap-4 sm:grid-cols-3">
          <label className="grid gap-2 text-sm font-medium">Service category
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3"><option>Electrical</option><option>Plumbing</option><option>Air conditioning</option><option>Appliance repair</option><option>Water purifier</option><option>Other</option><option>All</option></select>
          </label>
          <label className="grid gap-2 text-sm font-medium">Commission percent
            <Input type="number" min="0" max="100" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Approved rate" required />
          </label>
          <label className="grid gap-2 text-sm font-medium">Effective from
            <Input type="datetime-local" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required />
          </label>
          <div className="sm:col-span-3"><Button type="submit" disabled={saving === 'rule'}>{saving === 'rule' ? 'Saving rule…' : 'Create approved rule'}</Button></div>
        </form>
        <div className="mt-5 space-y-2">
          {(rules ?? []).map((rule) => (
            <div key={rule.Id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-3">
              <div><p className="font-semibold">{rule.serviceCategory_c} · {rule.ratePercent_c}%</p><p className="text-xs text-muted-foreground">Version {rule.version_c || '—'} · effective {rule.effectiveFrom_c ? new Date(rule.effectiveFrom_c).toLocaleString('en-IN') : '—'}</p></div>
              <span className="rounded-full bg-muted px-3 py-1 text-xs">{rule.isActive_c ? 'Active' : 'Inactive'}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="glass-panel rounded-[24px] p-5 sm:p-7">
        <h2 className="font-heading text-xl font-bold">Payment reconciliation</h2>
        <p className="mt-1 text-sm text-muted-foreground">Cash payments can be reconciled by an Admin. Online entries cannot be approved here until a server-side provider verification event sets the verified flag.</p>
        <div className="mt-4 space-y-3">
          {(payments ?? []).map((payment) => {
            const hasCredit = (walletRows ?? []).some((row) => row.paymentReference_c === String(payment.Id) && row.idempotencyKey_c === String(payment.Id) + ':earning');
            return (
              <article key={payment.Id} className="rounded-xl border border-border p-4">
                <p className="font-semibold">{payment.Name}</p>
                <p className="mt-1 text-xs text-muted-foreground">Job {payment.jobReference_c} · {payment.paymentMode_c} · {payment.paymentStatus_c}</p>
                <p className="mt-1 text-sm">Received ₹{Number(payment.amountReceived_c || 0).toFixed(2)} · Agent earning ₹{Number(payment.agentEarnings_c || 0).toFixed(2)} · Cash due ₹{Number(payment.cashPayableToAryanFix_c || 0).toFixed(2)}</p>
                <p className="mt-1 text-xs text-muted-foreground">Cash remittance: {payment.cashSettlementStatus_c || 'Not applicable'} · provider verified: {payment.providerVerified_c ? 'Yes' : 'No'}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {payment.paymentMode_c === 'Cash' && payment.paymentStatus_c !== 'Refunded' && <Button size="sm" disabled={saving === String(payment.Id) || hasCredit} onClick={() => reconcileCash(payment)}>{saving === String(payment.Id) ? 'Reconciling…' : hasCredit ? 'Earning credited' : payment.paymentStatus_c === 'Verified' ? 'Complete wallet credit' : 'Verify cash & credit earning'}</Button>}
                  {payment.paymentMode_c === 'Cash' && payment.paymentStatus_c === 'Verified' && payment.cashSettlementStatus_c !== 'Remitted' && <Button size="sm" variant="outline" disabled={saving === String(payment.Id)} onClick={() => setRemittanceConfirm(String(payment.Id))}>Mark cash remitted</Button>}
                  {payment.paymentMode_c === 'Online' && !payment.providerVerified_c && <span className="rounded-full bg-warning-muted px-3 py-2 text-xs font-semibold text-warning-foreground">Awaiting provider verification</span>}
                </div>
                {remittanceConfirm === String(payment.Id) && <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3"><p className="text-sm">Confirm that ₹{Number(payment.cashPayableToAryanFix_c || 0).toFixed(2)} has actually been remitted to AryanFix.</p><div className="mt-3 flex gap-2"><Button size="sm" onClick={() => markCashRemitted(payment)} disabled={saving === String(payment.Id)}>Confirm remittance</Button><Button size="sm" variant="outline" onClick={() => setRemittanceConfirm('')}>Cancel</Button></div></div>}
              </article>
            );
          })}
          {!paymentsLoading && !(payments ?? []).length && <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No payment submissions yet.</p>}
        </div>
      </section>
    </div>
  );
}
